import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ArrowLeft, Bell, Send, Megaphone, Wrench, Lightbulb, Sparkles, Users, User, Crown, Trash2, Eye, EyeOff, Loader2, Package, Check, X as XIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const UPDATE_TYPES = [
  { value: 'announcement', label: 'Announcement', icon: Megaphone, color: 'bg-blue-100 text-blue-800' },
  { value: 'system_update', label: 'System Update', icon: Wrench, color: 'bg-gray-100 text-gray-800' },
  { value: 'exam_tips', label: 'Exam Tips', icon: Lightbulb, color: 'bg-yellow-100 text-yellow-800' },
  { value: 'new_feature', label: 'New Feature', icon: Sparkles, color: 'bg-purple-100 text-purple-800' }
];

const PRIORITY_LEVELS = [
  { value: 'normal', label: 'Normal', color: 'bg-gray-100 text-gray-700' },
  { value: 'important', label: 'Important', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
];

const TARGET_OPTIONS = [
  { value: 'all_users', label: 'All Users', icon: Users },
  { value: 'by_grade', label: 'By Grade', icon: Users },
  { value: 'by_subscription', label: 'By Subscription (Premium / Free)', icon: Crown },
  { value: 'by_plans', label: 'By Specific Plans', icon: Package },
  { value: 'specific_user', label: 'Specific User', icon: User }
];

const GRADES = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];

const AdminUpdatesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [updateType, setUpdateType] = useState('announcement');
  const [priority, setPriority] = useState('normal');
  const [targetAudience, setTargetAudience] = useState('all_users');
  const [targetGrades, setTargetGrades] = useState([]);
  const [targetSubscription, setTargetSubscription] = useState('');
  const [targetPlanIds, setTargetPlanIds] = useState([]);
  const [plansPickerOpen, setPlansPickerOpen] = useState(false);
  const [plansList, setPlansList] = useState([]);
  const [targetUserIndos, setTargetUserIndos] = useState('');
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [verifyingUser, setVerifyingUser] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [sending, setSending] = useState(false);
  const [alsoSendEmail, setAlsoSendEmail] = useState(false);
  
  // Updates list state
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [updateToDelete, setUpdateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUpdates();
    fetchStats();
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    // Populates the BY_PLANS multi-select picker. Safe to fetch on
    // mount — payload is tiny (id/name/grade/price/is_archived).
    try {
      const res = await axios.get(`${API}/updates/admin/plans-picker`);
      setPlansList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to fetch plans picker:', e);
    }
  };

  // Case-insensitive INDoS verify — shows admin who they're about to
  // broadcast to BEFORE sending. Prevents "sent to wrong INDoS" mistakes
  // when the admin has a similar-looking number typed from memory.
  const verifyIndos = async () => {
    const value = targetUserIndos.trim();
    if (!value) return;
    setVerifyingUser(true);
    setVerifyError('');
    setVerifiedUser(null);
    try {
      const res = await axios.get(`${API}/updates/admin/lookup-user`, { params: { indos: value } });
      setVerifiedUser(res.data);
      // Snap the input to the canonical INDoS so what the admin sees
      // in the field matches what will be sent + logged.
      if (res.data?.indos_number) setTargetUserIndos(res.data.indos_number);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setVerifyError(typeof detail === 'string' ? detail : 'Lookup failed');
    } finally {
      setVerifyingUser(false);
    }
  };

  // Reset verify state whenever the audience changes away from
  // specific_user, or the admin edits the INDoS after verifying.
  useEffect(() => {
    if (targetAudience !== 'specific_user') {
      setVerifiedUser(null);
      setVerifyError('');
    }
  }, [targetAudience]);
  useEffect(() => {
    // If admin edits after verifying, invalidate the confirmation so
    // they can't confirm-then-tamper-then-send.
    if (verifiedUser && targetUserIndos.trim().toUpperCase() !== (verifiedUser.indos_number || '').toUpperCase()) {
      setVerifiedUser(null);
      setVerifyError('');
    }
  }, [targetUserIndos, verifiedUser]);

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/updates/admin/all?per_page=50`);
      setUpdates(response.data.updates || []);
    } catch (error) {
      console.error('Failed to fetch updates:', error);
      toast.error('Failed to load updates');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/updates/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleGradeToggle = (grade) => {
    setTargetGrades(prev => 
      prev.includes(grade) 
        ? prev.filter(g => g !== grade)
        : [...prev, grade]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !message.trim()) {
      toast.error('Please fill in title and message');
      return;
    }
    
    if (targetAudience === 'by_grade' && targetGrades.length === 0) {
      toast.error('Please select at least one grade');
      return;
    }
    
    if (targetAudience === 'by_subscription' && !targetSubscription) {
      toast.error('Please select a subscription type');
      return;
    }

    if (targetAudience === 'by_plans' && targetPlanIds.length === 0) {
      toast.error('Please pick at least one plan');
      return;
    }

    if (targetAudience === 'specific_user' && !targetUserIndos.trim()) {
      toast.error('Please enter the user\'s INDoS number');
      return;
    }

    // Require successful verification before sending — prevents typos
    // being broadcast to the wrong user. verifiedUser is invalidated
    // whenever the input diverges from the last verified INDoS.
    if (targetAudience === 'specific_user' && !verifiedUser) {
      toast.error('Please click Verify to confirm the target user before sending');
      return;
    }
    
    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        message: message.trim(),
        update_type: updateType,
        priority,
        target_audience: targetAudience,
        target_grades: targetAudience === 'by_grade' ? targetGrades : null,
        target_subscription: targetAudience === 'by_subscription' ? targetSubscription : null,
        target_plan_ids: targetAudience === 'by_plans' ? targetPlanIds : null,
        target_user_indos: targetAudience === 'specific_user' ? targetUserIndos.trim() : null,
        also_send_email: alsoSendEmail
      };
      
      const response = await axios.post(`${API}/updates/admin/create`, payload);
      const emailMsg = response.data.emails_sent > 0 ? ` + ${response.data.emails_sent} email(s)` : '';
      toast.success(`Update sent to ${response.data.sent_to_count} user(s)${emailMsg}`);

      // Surface email delivery failures explicitly so a silent
      // `emails_sent: 0` doesn't hide a real broadcast failure
      // (iter 101 incident — malformed `is_active` on the template).
      if (response.data.email_warning) {
        toast.warning('Email delivery issue', {
          description: response.data.email_warning,
          duration: 8000,
        });
      }
      
      // Reset form
      setTitle('');
      setMessage('');
      setUpdateType('announcement');
      setPriority('normal');
      setTargetAudience('all_users');
      setTargetGrades([]);
      setTargetSubscription('');
      setTargetPlanIds([]);
      setTargetUserIndos('');
      setVerifiedUser(null);
      setVerifyError('');
      
      // Refresh list
      fetchUpdates();
      fetchStats();
    } catch (error) {
      console.error('Failed to send update:', error);
      toast.error(error.response?.data?.detail || 'Failed to send update');
    } finally {
      setSending(false);
    }
  };

  const handleToggleActive = async (updateId, currentStatus) => {
    try {
      await axios.put(`${API}/updates/admin/${updateId}/toggle`);
      toast.success(currentStatus ? 'Update deactivated' : 'Update activated');
      fetchUpdates();
      fetchStats();
    } catch (error) {
      toast.error('Failed to toggle update status');
    }
  };

  const handleDeleteClick = (update) => {
    setUpdateToDelete(update);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!updateToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/updates/admin/${updateToDelete.id}`);
      toast.success('Update deleted');
      setDeleteConfirmOpen(false);
      setUpdateToDelete(null);
      fetchUpdates();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete update');
    } finally {
      setDeleting(false);
    }
  };

  const getTypeConfig = (type) => UPDATE_TYPES.find(t => t.value === type) || UPDATE_TYPES[0];
  const getPriorityConfig = (p) => PRIORITY_LEVELS.find(l => l.value === p) || PRIORITY_LEVELS[0];
  const getTargetLabel = (update) => {
    if (update.target_audience === 'all_users') return 'All Users';
    if (update.target_audience === 'by_grade') return `Grades: ${update.target_grades?.join(', ')}`;
    if (update.target_audience === 'by_subscription') return `${update.target_subscription === 'paid' ? 'Premium' : 'Free'} Users`;
    if (update.target_audience === 'by_plans') {
      // Resolve plan ids to names using the picker cache; fall back to
      // raw id when a plan was hard-deleted between send and view.
      const ids = Array.isArray(update.target_plan_ids) ? update.target_plan_ids : [];
      if (ids.length === 0) return 'Plans (none)';
      const names = ids.map(id => plansList.find(p => p.id === id)?.name || id);
      if (names.length <= 2) return `Plans: ${names.join(', ')}`;
      return `Plans: ${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
    }
    if (update.target_audience === 'specific_user') return `User: ${update.target_user_indos}`;
    return 'Unknown';
  };

  return (
    <Layout>
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/tools')} className="text-gray-600">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Admin Tools
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <Bell className="w-8 h-8 text-[#0A7EA4]" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Updates</h1>
          <p className="text-gray-600">Send announcements and notifications to users</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#0A7EA4]">{stats.total}</p>
              <p className="text-sm text-gray-600">Total Updates</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              <p className="text-sm text-gray-600">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-400">{stats.inactive}</p>
              <p className="text-sm text-gray-600">Inactive</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.by_type?.announcement || 0}</p>
              <p className="text-sm text-gray-600">Announcements</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compose Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Compose Update
            </CardTitle>
            <CardDescription>Create a new update to send to users</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter update title..."
                  maxLength={200}
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter your message..."
                  rows={4}
                />
              </div>

              {/* Type & Priority Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={updateType} onValueChange={setUpdateType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UPDATE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_LEVELS.map(level => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Target Audience */}
              <div>
                <Label>Target Audience</Label>
                <Select value={targetAudience} onValueChange={setTargetAudience}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TARGET_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <option.icon className="w-4 h-4" />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Conditional Target Fields */}
              {targetAudience === 'by_grade' && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="mb-2 block">Select Grades</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {GRADES.map(grade => (
                      <div key={grade} className="flex items-center gap-2">
                        <Checkbox
                          id={`grade-${grade}`}
                          checked={targetGrades.includes(grade)}
                          onCheckedChange={() => handleGradeToggle(grade)}
                        />
                        <label htmlFor={`grade-${grade}`} className="text-sm cursor-pointer">
                          {grade}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {targetAudience === 'by_subscription' && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="mb-2 block">Select Subscription Type</Label>
                  <Select value={targetSubscription} onValueChange={setTargetSubscription}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select subscription..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Premium Users</SelectItem>
                      <SelectItem value="free">Free Users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {targetAudience === 'by_plans' && (
                <div className="p-3 bg-gray-50 rounded-lg" data-testid="target-plans-block">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Select Plans</Label>
                    {targetPlanIds.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setTargetPlanIds([])}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                        data-testid="clear-plans-btn"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  {/* Selected plan chips — visible at rest so the admin
                      always sees the current scope without opening the picker. */}
                  {targetPlanIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="selected-plan-chips">
                      {targetPlanIds.map(id => {
                        const p = plansList.find(x => x.id === id);
                        return (
                          <Badge
                            key={id}
                            variant="outline"
                            className="text-xs bg-white gap-1 pl-2 pr-1 py-0.5"
                          >
                            <span>{p?.name || id}</span>
                            <button
                              type="button"
                              onClick={() => setTargetPlanIds(prev => prev.filter(x => x !== id))}
                              className="text-gray-400 hover:text-red-600"
                              data-testid={`remove-plan-${id}`}
                              aria-label={`Remove ${p?.name || id}`}
                            >
                              <XIcon className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}

                  <Popover open={plansPickerOpen} onOpenChange={setPlansPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between"
                        data-testid="plans-picker-trigger"
                      >
                        <span className="text-sm text-gray-700">
                          {targetPlanIds.length === 0
                            ? 'Pick one or more plans…'
                            : `${targetPlanIds.length} plan${targetPlanIds.length === 1 ? '' : 's'} selected`}
                        </span>
                        <Package className="w-4 h-4 text-gray-500" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <PlansCombobox
                        plans={plansList}
                        selectedIds={targetPlanIds}
                        onToggle={(id) => setTargetPlanIds(prev =>
                          prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                        )}
                      />
                    </PopoverContent>
                  </Popover>

                  <p className="text-xs text-gray-500 mt-2">
                    Targets users whose plan matches any of the selected. Archived plans are dimmed but selectable — useful for grandfathering messages.
                  </p>
                </div>
              )}

              {targetAudience === 'specific_user' && (
                <div className="p-3 bg-gray-50 rounded-lg space-y-2" data-testid="target-specific-user-block">
                  <Label htmlFor="userIndos">User INDoS Number</Label>
                  <div className="flex gap-2">
                    <Input
                      id="userIndos"
                      value={targetUserIndos}
                      onChange={(e) => setTargetUserIndos(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); verifyIndos(); } }}
                      placeholder="e.g. 17EL1129 (case-insensitive)"
                      className="flex-1 font-mono uppercase"
                      autoComplete="off"
                      data-testid="target-user-indos-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={verifyIndos}
                      disabled={verifyingUser || !targetUserIndos.trim()}
                      data-testid="verify-indos-btn"
                    >
                      {verifyingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                    </Button>
                  </div>

                  {/* Verified user card — shows Name / INDoS / Grade /
                      Email so the admin can eyeball before hitting send.
                      Also blocks the send button until verify succeeds
                      (see handleSubmit guard). */}
                  {verifiedUser && (
                    <div
                      className="mt-2 p-3 border border-emerald-200 bg-emerald-50/60 rounded-md"
                      data-testid="verified-user-card"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Check className="w-4 h-4 text-emerald-700" />
                        <span className="text-sm font-semibold text-emerald-900">Verified user</span>
                        {verifiedUser.role !== 'user' && (
                          <Badge variant="outline" className="text-[10px] uppercase bg-white">{verifiedUser.role.replace('_', ' ')}</Badge>
                        )}
                      </div>
                      <dl className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-0.5 text-sm text-slate-700">
                        <dt className="text-slate-500">Name</dt><dd className="font-medium">{verifiedUser.name}</dd>
                        <dt className="text-slate-500">INDoS</dt><dd className="font-mono">{verifiedUser.indos_number}</dd>
                        <dt className="text-slate-500">Grade</dt><dd>{verifiedUser.grade}</dd>
                        <dt className="text-slate-500">Email</dt><dd className="truncate">{verifiedUser.email}</dd>
                      </dl>
                    </div>
                  )}

                  {verifyError && !verifiedUser && (
                    <p className="text-xs text-rose-600 mt-1" data-testid="verify-indos-error">
                      {verifyError}
                    </p>
                  )}

                  {!verifiedUser && !verifyError && (
                    <p className="text-xs text-slate-500">
                      Click <strong>Verify</strong> to preview the user before sending.
                    </p>
                  )}
                </div>
              )}

              {/* Also send via email */}
              <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/50">
                <input
                  type="checkbox"
                  id="alsoSendEmail"
                  checked={alsoSendEmail}
                  onChange={(e) => setAlsoSendEmail(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  data-testid="also-send-email-checkbox"
                />
                <label htmlFor="alsoSendEmail" className="flex-1 cursor-pointer">
                  <span className="text-sm font-medium text-gray-700">Also send via Email</span>
                  <p className="text-xs text-gray-500 mt-0.5">Send this update as an email to all targeted users with email addresses</p>
                </label>
              </div>

              <Button type="submit" disabled={sending} className="w-full bg-[#0A7EA4] hover:bg-[#0A7EA4]/90">
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> {alsoSendEmail ? 'Send Update + Email' : 'Send Update'}</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Updates History */}
        <Card>
          <CardHeader>
            <CardTitle>Sent Updates</CardTitle>
            <CardDescription>History of all updates sent</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                <p className="text-gray-500 mt-2">Loading updates...</p>
              </div>
            ) : updates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No updates sent yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {updates.map(update => {
                  const typeConfig = getTypeConfig(update.update_type);
                  const priorityConfig = getPriorityConfig(update.priority);
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <div
                      key={update.id}
                      className={`p-3 rounded-lg border ${update.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge className={typeConfig.color}>
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {typeConfig.label}
                            </Badge>
                            <Badge className={priorityConfig.color}>
                              {priorityConfig.label}
                            </Badge>
                            {!update.is_active && (
                              <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900 truncate">{update.title}</h4>
                          <p className="text-sm text-gray-600 line-clamp-2">{update.message}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span>To: {getTargetLabel(update)}</span>
                            <span>•</span>
                            <span>{update.sent_to_count} user(s)</span>
                            <span>•</span>
                            <span>{new Date(update.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleActive(update.id, update.is_active)}
                            title={update.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {update.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClick(update)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Update</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this update? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {updateToDelete && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium">{updateToDelete.title}</p>
              <p className="text-sm text-gray-600 truncate">{updateToDelete.message}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

/**
 * Multi-select combobox for the BY_PLANS picker.
 *
 * - Searchable via shadcn Command (title + grade match).
 * - Grouped by grade with a header row per group.
 * - Archived plans are dimmed but selectable (grandfather messaging).
 * - Currency + grade prefix on the label so admins can eyeball scope.
 * - `null` grade plans (rare — legacy or platform-wide) get their own
 *   "Other" group so they don't disappear.
 */
const PlansCombobox = ({ plans, selectedIds, onToggle }) => {
  // Group plans by grade, sort groups alphabetically, then archived
  // suffix. Grade `null` becomes "Other".
  const grouped = React.useMemo(() => {
    const map = new Map();
    plans.forEach(p => {
      const key = p.grade || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    // Preferred grade ordering matches the picker in the User Management
    // filter (matches ALL_GRADES on the backend). Unknown keys fall to
    // the end.
    const order = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG', 'Other'];
    return order
      .filter(g => map.has(g))
      .map(g => ({ grade: g, items: map.get(g).sort((a, b) => (a.price || 0) - (b.price || 0)) }))
      .concat(
        Array.from(map.keys())
          .filter(k => !order.includes(k))
          .sort()
          .map(g => ({ grade: g, items: map.get(g) }))
      );
  }, [plans]);

  const formatPrice = (p) => (p == null ? '' : `₹${p.toLocaleString('en-IN')}`);

  return (
    <Command shouldFilter={true}>
      <CommandInput placeholder="Search plans by name or grade…" data-testid="plans-combobox-search" />
      <CommandList className="max-h-72">
        <CommandEmpty>No plans match.</CommandEmpty>
        {grouped.map(group => (
          <CommandGroup key={group.grade} heading={group.grade}>
            {group.items.map(p => {
              const selected = selectedIds.includes(p.id);
              const archived = !!p.is_archived;
              return (
                <CommandItem
                  key={p.id}
                  // Combine name + grade + id so search matches any of them.
                  value={`${p.name} ${group.grade} ${p.id}`}
                  onSelect={() => onToggle(p.id)}
                  className={`cursor-pointer ${archived ? 'opacity-60' : ''}`}
                  data-testid={`plans-combobox-item-${p.id}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${selected ? 'opacity-100 text-teal-600' : 'opacity-0'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 flex items-center gap-2">
                      <span className="truncate">{p.name}</span>
                      {archived && (
                        <Badge variant="outline" className="text-[10px] py-0 h-4 px-1">
                          archived
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{formatPrice(p.price)}</div>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
};

export default AdminUpdatesPage;
